import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { mockLGAs } from '@/data/nigerianStates';
import FireworkAnimation from '../components/join/FireworkAnimation';
import LoginForm from '../components/join/LoginForm';
import RegistrationForm from '../components/join/RegistrationForm';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';

const nigerianStates = [
  "Lagos", "Ogun", "Oyo", "Ondo", "Ekiti", "Kwara", "Kogi", "Delta", "Edo", "Osun"
];

const yorubaSubgroups = [
  "Oyo", "Egba", "Ijebu", "Ekiti", "Ijesha", "Ife", "Ondo", "Owo", "Awori", 
  "Egbado", "Ibarapa", "Igbomina", "Okun", "Akoko", "Ilaje", "Itsekiri", 
  "Ana", "Idaisa", "Sabe", "Ketu", "Mahi", "Ibadan", "Ibolo"
];

type RegistrationForm = {
  name: string;
  email: string;
  phone: string;
  state: string;
  localGovernment: string;
  yorubaSubgroup: string;
  ageGroup: string;
  gender: string;
  password: string;
  confirmPassword: string;
}

const JoinPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState<RegistrationForm>({
    name: '',
    email: '',
    phone: '',
    state: searchParams.get('state') || '',
    localGovernment: searchParams.get('lga') || '',
    yorubaSubgroup: '',
    ageGroup: '',
    gender: '',
    password: '',
    confirmPassword: '',
  });

  const [availableLGAs, setAvailableLGAs] = useState<string[]>([]);
  const [isLogin, setIsLogin] = useState(true);
  const [showFireworks, setShowFireworks] = useState(false);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [referrerId, setReferrerId] = useState<string | null>(null);
  
  // IMMEDIATE check for special ID in URL - highest priority
  useEffect(() => {
    // Direct check and redirect immediately without conditions
    const currentUrl = window.location.href;
    const referrer = searchParams.get('referrer');
    
    // Check both URL and referrer for the special pattern
    if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195') || 
        referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
      console.log("JoinPage: IMMEDIATE URL CHECK - Redirecting to final destination");
      window.location.replace('https://www.omooduduwa.org/join');
    }
  }, []);
  
  // Regular check with interval as backup
  useEffect(() => {
    const checkForRedirect = () => {
      const currentUrl = window.location.href;
      const referrer = searchParams.get('referrer');
      
      // Always check for the special ID pattern first
      if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195') || 
          referrer === '21d11b63-7b91-46fa-8c91-a30744574195') {
        console.log("JoinPage: SPECIAL PATTERN DETECTED - Redirecting immediately");
        window.location.replace('https://www.omooduduwa.org/join');
        return true;
      }
      
      return false;
    };
    
    // Execute immediately
    const redirected = checkForRedirect();
    
    // Also set up a recurring check to handle edge cases
    if (!redirected) {
      const intervalCheck = setInterval(() => {
        const redirected = checkForRedirect();
        if (redirected) clearInterval(intervalCheck);
      }, 500); // Check every half second
      
      return () => clearInterval(intervalCheck);
    }
  }, [searchParams, location]);
  
  // Set available LGAs if state is pre-selected from query params
  useEffect(() => {
    if (formData.state) {
      // Safe access with type assertion
      setAvailableLGAs(formData.state in mockLGAs ? mockLGAs[formData.state as keyof typeof mockLGAs] || [] : []);
    }
  }, [formData.state]);
  
  // Check for referrer info - updated to load by user ID instead of email
  useEffect(() => {
    const referrerParam = searchParams.get('referrer');
    if (referrerParam) {
      setReferrerId(referrerParam);
      
      // Skip fetching referrer info for the special case
      if (referrerParam === '21d11b63-7b91-46fa-8c91-a30744574195') {
        return;
      }
      
      const fetchReferrerInfo = async () => {
        try {
          // Try to fetch directly from community_members table
          const { data, error } = await supabase
            .from('community_members')
            .select('full_name, nickname')
            .eq('id', referrerParam)
            .single();
          
          if (data && !error) {
            const displayName = data.nickname || data.full_name;
            setReferrerName(displayName);
            toast.info(`You were invited by ${displayName}`);
          }
        } catch (err) {
          console.error('Error fetching referrer info:', err);
        }
      };
      
      fetchReferrerInfo();
    }
  }, [searchParams]);
  
  // Redirect to community page if already logged in
  useEffect(() => {
    if (user) {
      navigate('/community');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Show fireworks animation after a short delay when page loads
    const timer = setTimeout(() => {
      setShowFireworks(true);
      setTimeout(() => setShowFireworks(false), 3000); // Hide after 3 seconds
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'state') {
      // Reset LGA when state changes
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        localGovernment: ''
      }));
      
      // Set available LGAs based on selected state
      setAvailableLGAs(value in mockLGAs ? mockLGAs[value as keyof typeof mockLGAs] || [] : []);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="bg-gray-950 min-h-screen bg-cover bg-no-repeat bg-center"
        style={{
          backgroundImage: 'url(/lovable-uploads/c991389f-c2d1-4e8c-aee1-419cb7b0f3f0.png)',
          backgroundSize: '100% auto',
          backgroundPosition: 'top center',
          backgroundAttachment: 'scroll',
          backgroundColor: '#000'
        }}>
      <div className="relative">
        <FireworkAnimation show={showFireworks} />

        <div className="bg-omoduduwa-indigo/80 backdrop-blur-md text-white py-16">
          <div className="container-custom">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {isLogin ? 'Welcome Back' : 'Join Omo Oduduwa'}
            </motion.h1>
            <motion.p 
              className="text-xl mt-4 text-gray-200"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {isLogin 
                ? 'Sign in to access your account and connect with the community' 
                : 'Become a member of Omo Oduduwa'}
            </motion.p>
            
            {referrerName && (
              <motion.div 
                className="mt-4 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-md inline-block"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <p className="text-white text-sm">Invited by: <span className="font-semibold">{referrerName}</span></p>
              </motion.div>
            )}
          </div>
        </div>

        <div className="container-custom py-16">
          <motion.div 
            className="max-w-2xl mx-auto bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-orange-300/30 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex">
              <button 
                className={`flex-1 text-center py-4 font-medium text-lg transition-all ${isLogin ? 'bg-omoduduwa-terracotta text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                onClick={() => setIsLogin(true)}
              >
                Sign In
              </button>
              <button 
                className={`flex-1 text-center py-4 font-medium text-lg transition-all ${!isLogin ? 'bg-omoduduwa-terracotta text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                onClick={() => setIsLogin(false)}
              >
                Register
              </button>
            </div>

            <div className="p-8">
              {isLogin ? (
                <LoginForm 
                  onToggleForm={() => setIsLogin(false)}
                />
              ) : (
                <RegistrationForm 
                  formData={formData}
                  handleChange={handleChange}
                  nigerianStates={nigerianStates}
                  availableLGAs={availableLGAs}
                  yorubaSubgroups={yorubaSubgroups}
                  setShowFireworks={setShowFireworks}
                  referrerId={referrerId}
                />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
